import { createContext, useContext, useState, useMemo, type ReactNode } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Profile } from '../types';

interface SupabaseContextType {
    supabase: SupabaseClient | null;
    initSupabase: (url: string, key: string) => void;
    activeProfile: Profile | null;
    selectProfile: (profile: Profile | null) => void;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export const SupabaseProvider = ({ children }: { children: ReactNode }) => {
    const [supabase, setSupabase] = useState<SupabaseClient | null>(() => {
        const rawConfig = localStorage.getItem('kmdb_config');
        if (!rawConfig) return null;
        try {
            const config = JSON.parse(rawConfig);
            if (config.supabaseUrl && config.supabaseAnonKey) {
                return createClient(config.supabaseUrl, config.supabaseAnonKey);
            }
        } catch (e) { return null; }
        return null;
    });

    const [activeProfile, setActiveProfile] = useState<Profile | null>(() => {
        const saved = localStorage.getItem('kmdb_active_profile');
        return saved ? JSON.parse(saved) : null;
    });

    const initSupabase = (url: string, key: string) => {
        const config = { supabaseUrl: url, supabaseAnonKey: key };
        localStorage.setItem('kmdb_config', JSON.stringify(config));
        setSupabase(createClient(url, key));
    };

    const selectProfile = (profile: Profile | null) => {
        setActiveProfile(profile);
        if (profile) {
            localStorage.setItem('kmdb_active_profile', JSON.stringify(profile));
        } else {
            localStorage.removeItem('kmdb_active_profile');
        }
    };

    const value = useMemo(() => ({
        supabase,
        initSupabase,
        activeProfile,
        selectProfile
    }), [supabase, activeProfile]);

    return (
        <SupabaseContext.Provider value={value}>
            {children}
        </SupabaseContext.Provider>
    );
};

export const useSupabase = () => {
    const context = useContext(SupabaseContext);
    if (context === undefined) {
        throw new Error('useSupabase must be used within a SupabaseProvider');
    }
    return context;
};